import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import { EllipsisVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';

import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { SortableHeader } from '@/components/ui/table/sortable-header';
import type { Member } from '@/shared/types/member';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';

import { modelIconResolver, providerNameMapper } from './helper';
import { LanguageModel } from '../../types';

interface ColumnsProps {
  onEdit: (model: LanguageModel) => void;
  onDelete: (model: LanguageModel) => void;
  onToggleEnabled: (model: LanguageModel) => void;
  getMember: (userId: string) => Member | undefined;
  canWrite: boolean;
  canDelete: boolean;
}

export const createColumns = ({
  onEdit,
  onDelete,
  onToggleEnabled,
  getMember,
  canWrite,
  canDelete,
}: ColumnsProps): ColumnDef<LanguageModel>[] => [
  {
    id: 'icon',
    enableHiding: false,
    enableSorting: false,
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM', className: 'pl-4' },
    cell: ({ row }) => modelIconResolver(row.original.provider, 16),
  },
  {
    id: 'provider',
    accessorFn: (row) => providerNameMapper(row.provider),
    enableHiding: false,
    ...COLUMN_PRESETS.LG,
    meta: { preset: 'LG' },
    header: ({ column }) => (
      <SortableHeader column={column}>
        {t('developer_tools.language_models_provider')}
      </SortableHeader>
    ),
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <span className="min-w-0 truncate font-medium">
          {providerNameMapper(row.original.provider)}
        </span>
        <RowActionIndicator action="edit" />
      </div>
    ),
  },
  {
    id: 'model',
    accessorKey: 'model',
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('developer_tools.language_models_model')}</SortableHeader>
    ),
    cell: ({ row }) => <span className="truncate">{row.original.model}</span>,
  },
  {
    id: 'created_by',
    accessorFn: (row) => {
      const member = row.created_by ? getMember(row.created_by) : undefined;
      return member?.name || '';
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'common.created_by' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('common.created_by')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const createdBy = row.original.created_by;
      if (!createdBy) {
        return <span className="text-foreground-muted">-</span>;
      }
      const member = getMember(createdBy);
      const fallback = member?.name?.charAt(0) || '?';
      return (
        <div className="flex items-center gap-2">
          <Avatar size="xs">
            <AvatarImage src={member?.avatar || ''} alt={member?.name || ''} />
            <AvatarFallback>{fallback}</AvatarFallback>
          </Avatar>
          <span className="truncate">{member?.name || '-'}</span>
        </div>
      );
    },
  },
  {
    id: 'updated',
    accessorKey: 'updated',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'common.last_updated' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('common.last_updated')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const updated = row.original.updated;
      return updated ? (
        <TimestampTooltip timestamp={updated}>{timeFromNow(updated)}</TimestampTooltip>
      ) : (
        '-'
      );
    },
  },
  {
    id: 'last_used_at',
    accessorKey: 'last_used_at',
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD', label: 'common.last_used' },
    header: ({ column }) => (
      <SortableHeader column={column}>{t('common.last_used')}</SortableHeader>
    ),
    cell: ({ row }) => {
      const lastUsed = row.original.last_used_at;
      return lastUsed ? (
        <TimestampTooltip timestamp={lastUsed}>{timeFromNow(lastUsed)}</TimestampTooltip>
      ) : (
        '-'
      );
    },
  },
  {
    id: 'active',
    accessorKey: 'enabled',
    enableSorting: false,
    ...COLUMN_PRESETS.MD,
    size: 112,
    maxSize: 128,
    meta: { preset: 'MD', label: 'common.active' },
    header: () => t('common.active'),
    cell: ({ row }) => (
      <Switch
        checked={row.original.enabled}
        onCheckedChange={() => onToggleEnabled(row.original)}
        disabled={!canWrite}
        onClick={(e) => e.stopPropagation()}
      />
    ),
  },
  {
    id: 'actions',
    enableHiding: false,
    enableSorting: false,
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
    cell: ({ row }) => {
      const model = row.original;
      return (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost-neutral"
              size="icon-sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <EllipsisVerticalIcon size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem
              onSelect={() => onEdit(model)}
              className="flex items-center gap-2"
              disabled={!canWrite}
            >
              <PencilIcon size={16} className="text-foreground-muted" />
              {t('common.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => onDelete(model)}
              variant="destructive"
              className="flex items-center gap-2"
              disabled={!canDelete}
            >
              <Trash2Icon size={16} />
              {`${t('common.delete')}…`}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
